import {Guest, Reservation} from './types';
import {Exemptions} from '../hooks';

function getHousingName(reservation: any = {}): string {
  const name = reservation?.housing?.name;

  if (!name) {
    return '';
  }
  return name;
}

function getHousingPicture(reservation: any = {}): string {
  const src = reservation?.housing?.picture?.src;

  if (!src) {
    return '';
  }
  return src;
}

function getHousingManagerOrigin(reservation: any = {}): string {
  return reservation?.housing?.manager_origin || '';
}

function getReservationDate(date?: any) {
  if (!date) {
    return null;
  }

  const pureDate = date.slice(0, -1);
  return new Date(pureDate);
}

function getCheckInDate(reservation: any = {}) {
  const date = reservation?.check_in_date;
  return getReservationDate(date);
}

function getGuestPoliceStatusInCode(guest?: Guest) {
  return guest?.statuses?.police?.in?.code || '';
}

function getHasAnyPoliceCompleteGuest(reservation: any = {}) {
  const members = getMembers(reservation);

  return members.some((member: Guest) => {
    const memberStatus = getGuestPoliceStatusInCode(member);
    return memberStatus === 'COMPLETE';
  });
}

function getCheckOutDate(reservation: any = {}) {
  const date = reservation?.check_out_date;
  return getReservationDate(date);
}

function getHousingCountryCode(reservation: any = {}): string {
  const code = reservation?.housing?.location?.country?.code;

  if (!code) {
    return '';
  }
  return code;
}

function getNumberOfGuests(reservation: any = {}, defaultValue: number = 0): number {
  const numberOfGuests = reservation?.guest_group?.number_of_guests;

  if (!numberOfGuests) {
    return defaultValue;
  }
  return numberOfGuests;
}

function getMembers(reservation: any = {}): Guest[] {
  const members = reservation?.guest_group?.members;

  if (!members) {
    return [];
  }
  return members;
}

function getHasGuestMembers(reservation: any = {}) {
  if (!reservation) {
    return false;
  }

  const members = getMembers(reservation);
  return Boolean(members.length);
}

const getIsAllGuestsRegistered = (reservation: any = {}) => {
  if (!reservation) {
    return false;
  }

  const groupMembersNumber = getMembers(reservation).length;
  const numberOfGuests = getNumberOfGuests(reservation);
  return groupMembersNumber === numberOfGuests;
};

function getGuestLeaderId(reservation: any = {}) {
  if (!reservation) {
    return '';
  }
  return reservation?.guest_group?.leader_id;
}

function getHasGuestLeader(reservation: any = {}) {
  return Boolean(getGuestLeaderId(reservation));
}

function getHasOneMember(reservation: any = {}) {
  return Boolean(reservation?.guest_group?.members?.length === 1);
}

function getIsContractEnabled(reservation: any = {}) {
  const isEnabled = reservation?.housing?.is_contract_enabled;
  return Boolean(isEnabled);
}

function getIsStatEnabled(reservation: any = {}) {
  const isEnabled = reservation?.housing?.is_stat_registration_enabled;
  return Boolean(isEnabled);
}

function getStatType(reservation: any = {}) {
  const type = reservation?.housing?.stat_account?.type;

  if (!type) {
    return '';
  }
  return type;
}

function getStatTypeIfStatActive(reservation: any = {}) {
  if (!getIsStatEnabled(reservation)) {
    return '';
  }
  return getStatType(reservation);
}

function getIsCaptureStatFieldsEnabled(reservation: any = {}) {
  return Boolean(reservation?.housing?.is_capture_stat_fields_enabled);
}

function getGuestGroupType(reservation: any = {}) {
  if (!reservation) {
    return '';
  }
  return reservation?.guest_group?.type;
}

function getSeasonsIds(reservation: any = {}) {
  if (!reservation) {
    return [];
  }
  return reservation?.housing?.seasons || [];
}

function getHasSuperHog(reservation: any = {}) {
  const hasSuperHub = reservation?.housing?.has_host_protection_insurance && reservation?.housing?.host_protection_insurance_provider === 'Superhog';
  return hasSuperHub;
}

type GetHasData = {
  reservation?: Reservation;
  exemptions?: Exemptions;
  housing?: any;
};
function getHasTaxes({reservation, housing, exemptions}: GetHasData) {
  const data = reservation ? reservation.housing : housing;
  if (!data) {
    return false;
  }

  return Boolean(data.seasons?.length) && !exemptions?.taxExemption;
}

type CheckHasSelfieIdentity = {
  isLeaderGuest: boolean;
  isVerifyDocumentAndSelfie: boolean;
  isBiomatchForAllGuests: boolean;
};
function checkHasSelfieIdentity({
  isLeaderGuest,
  isVerifyDocumentAndSelfie,
  isBiomatchForAllGuests,
}: CheckHasSelfieIdentity) {
  if (isLeaderGuest) {
    return Boolean(isVerifyDocumentAndSelfie);
  }
  return Boolean(isBiomatchForAllGuests && isVerifyDocumentAndSelfie);
}

type CheckHasScanDocument = {
  isLeaderGuest: boolean;
  isVerifyOnlyDocument: boolean;
  isBiomatchForAllGuests: boolean;
};
function checkHasScanDocument({
  isLeaderGuest,
  isVerifyOnlyDocument,
  isBiomatchForAllGuests,
}: CheckHasScanDocument) {
  if (isLeaderGuest) {
    return Boolean(isVerifyOnlyDocument);
  }
  return Boolean(isBiomatchForAllGuests && isVerifyOnlyDocument);
}

export {
  getHousingManagerOrigin,
  getHasTaxes,
  checkHasSelfieIdentity,
  checkHasScanDocument,
  getIsCaptureStatFieldsEnabled,
  getHousingName,
  getHousingPicture,
  getCheckInDate,
  getCheckOutDate,
  getHousingCountryCode,
  getNumberOfGuests,
  getMembers,
  getHasGuestMembers,
  getHasGuestLeader,
  getHasOneMember,
  getIsContractEnabled,
  getStatTypeIfStatActive,
  getStatType,
  getIsStatEnabled,
  getGuestGroupType,
  getGuestLeaderId,
  getHasAnyPoliceCompleteGuest,
  getIsAllGuestsRegistered,
  getSeasonsIds,
  getHasSuperHog
};
